import { useCallback, useEffect, useRef, useState } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  FAIconName,
  FireflyIconButton,
  Text,
} from '@equip.health/ui';
import { DateTime } from 'luxon';
import clapIcon from '~/assets/img/clap.png';
import { getGreeting, getLastUpdatedLabel, getPatientLabel } from './provider-dashboard.util';

interface ProviderDashboardGreetingHeaderProps {
  displayName: string;
  distinctPatientsCount?: number;
  isLoadingPatientsCount?: boolean;
  isErrorPatientsCount?: boolean;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

const ProviderDashboardGreetingHeader = ({
  displayName,
  distinctPatientsCount,
  isLoadingPatientsCount = false,
  isErrorPatientsCount = false,
  onRefresh,
  isRefreshing = false,
}: ProviderDashboardGreetingHeaderProps) => {
  const formattedDate = DateTime.now().toFormat('EEEE, MMMM d, yyyy');
  const [minutesSinceRefresh, setMinutesSinceRefresh] = useState<number>(0);
  const lastRefreshTime = useRef<number>(Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      const elapsed = Math.floor(
        (Date.now() - lastRefreshTime.current) / 60000,
      );
      setMinutesSinceRefresh(elapsed);
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = useCallback(() => {
    lastRefreshTime.current = Date.now();
    setMinutesSinceRefresh(0);
    onRefresh?.();
  }, [onRefresh]);

  return (
    <ChakraFlex
      alignItems="flex-start"
      data-testid="provider-dashboard-greeting-header"
      justifyContent="space-between"
      gap="24px"
    >
      <ChakraFlex alignItems="center" gap="18px">
        <img className="provider-dashboard__clap" src={clapIcon} alt="" />
        <ChakraBox>
          <Text
            variant="captionMedium"
            color="neutral.secondary"
            textTransform="uppercase"
            marginBottom="8px"
          >
            {formattedDate}
          </Text>
          <Text variant="h4" marginBottom="8px">
            {getGreeting()}, {displayName}
          </Text>
          <Text variant="body" color="neutral.secondary" maxWidth="720px">
            {isErrorPatientsCount || distinctPatientsCount === 0 ? (
              "Welcome to Equip! We're happy to have you on the team."
            ) : (
              <>
                You've made a difference in the lives of{' '}
                <strong>
                  {isLoadingPatientsCount
                    ? '—'
                    : (distinctPatientsCount ?? '—')}{' '}
                  {getPatientLabel(distinctPatientsCount)}
                </strong>{' '}
                in your time at Equip.
              </>
            )}
          </Text>
        </ChakraBox>
      </ChakraFlex>

      {onRefresh && (
        <ChakraFlex alignItems="center" gap="12px" flexShrink={0}>
          <Text variant="caption" color="neutral.secondary" whiteSpace="nowrap">
            {getLastUpdatedLabel(minutesSinceRefresh)}
          </Text>
          <FireflyIconButton
            ariaLabel="Refresh data"
            icon={FAIconName.arrowsRotateRegular}
            id="provider-dashboard-refresh"
            isLoading={isRefreshing}
            onClick={handleRefresh}
            variant="tertiary"
          />
        </ChakraFlex>
      )}
    </ChakraFlex>
  );
};

export default ProviderDashboardGreetingHeader;
